import {
  isFirebaseInitialized,
  socialService,
  unifiedGetDoc,
  unifiedUpdateDoc,
  unifiedDeleteDoc,
  unifiedAddDoc,
  unifiedCollection
} from '@watchlist/shared';
import { getCachedUserProfile } from '../utils/profileCache';


export interface CollaborationRequest {
  id: string;
  watchlistId: string;
  watchlistName: string;
  fromUserId: string;
  fromUserName: string;
  fromUserAvatar?: string;
  toUserId: string;
  toUserName?: string;
  toUserAvatar?: string;
  toUserEmail?: string;
  role: 'admin' | 'editor';
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  message?: string;
  createdAt: any;
  expiresAt?: any;
  metadata?: {
    watchlistMovieCount?: number;
    watchlistPosters?: string[];
    watchlistIsPrivate?: boolean;
  };
}

export class CollaborationRequestService {
  /**
   * Send a collaboration request instead of immediately adding collaborator
   */
  static async sendCollaborationRequest(
    watchlistId: string,
    fromUserId: string,
    toUserId: string,
    role: 'admin' | 'editor' = 'editor',
    message?: string
  ): Promise<string> {
    try {
      if (!isFirebaseInitialized()) {
        throw new Error('Firebase not initialized');
      }

      // Get watchlist details using unified getDoc for cross-platform support
      const watchlistDoc = await unifiedGetDoc('watchlists', watchlistId);

      if (!watchlistDoc.exists()) {
        throw new Error('Watchlist not found');
      }

      const watchlistData = watchlistDoc.data();

      // Check if sender has permission
      if (watchlistData.createdBy !== fromUserId &&
        !(watchlistData.collaborators || []).includes(fromUserId)) {
        throw new Error('Permission denied: You cannot send collaboration requests for this watchlist');
      }

      // Check if user is already a collaborator
      if ((watchlistData.collaborators || []).includes(toUserId)) {
        throw new Error('User is already a collaborator');
      }

      // Check if user is the owner
      if (watchlistData.createdBy === toUserId) {
        throw new Error('Cannot send collaboration request to the owner');
      }

      // Check for existing pending request
      const existingRequest = await this.getPendingRequest(watchlistId, toUserId);
      if (existingRequest) {
        throw new Error('The user has already been invited');
      }

      // Get user profiles
      const [fromProfile, toProfile] = await Promise.all([
        getCachedUserProfile(fromUserId),
        getCachedUserProfile(toUserId)
      ]);

      if (!fromProfile || !toProfile) {
        throw new Error('User profiles not found');
      }

      // Block invite to private profiles unless sender is already a follower
      if ((toProfile as any).privacy?.profileVisibility === 'private') {
        const isFollower = await socialService.isFollowing(fromUserId, toUserId);
        if (!isFollower) {
          throw new Error('This user has a private profile. You need to follow them before inviting them to collaborate.');
        }
      }

      // Calculate expiration (7 days from now)
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      // Create collaboration request
      const requestData: Omit<CollaborationRequest, 'id'> = {
        watchlistId,
        watchlistName: watchlistData.name || 'Untitled Watchlist',
        fromUserId,
        fromUserName: fromProfile.displayName || 'Unknown User',
        toUserId,
        role,
        status: 'pending',
        ...(message && { message }), // Only include message if it's defined
        ...(fromProfile.photoURL && { fromUserAvatar: fromProfile.photoURL }), // Only include if defined
        toUserName: toProfile.displayName || 'Unknown User',
        ...(toProfile.photoURL && { toUserAvatar: toProfile.photoURL }), // Only include if defined
        ...(toProfile.email && { toUserEmail: toProfile.email }), // Only include if defined
        createdAt: new Date().toISOString(),
        expiresAt: expiresAt.toISOString(),
        metadata: {
          watchlistMovieCount: watchlistData.movieDetails?.length || 0,
          watchlistPosters: watchlistData.movieDetails?.slice(0, 4)
            .map((m: any) => m.poster)
            .filter((poster: any): poster is string => !!poster) || [],
          watchlistIsPrivate: watchlistData.isPrivate || false
        }
      };

      console.log('📧 CollaborationRequestService: Creating collaboration request with data:', {
        watchlistId,
        watchlistName: watchlistData.name,
        isPrivate: watchlistData.isPrivate,
        fromUserId,
        toUserId,
        requestData
      });

      const requestId = await unifiedAddDoc('collaborationRequests', requestData);

      console.log('✅ CollaborationRequestService: Collaboration request created successfully:', {
        requestId,
        recipientId: toUserId,
        actorId: fromUserId,
        watchlistId,
        watchlistName: watchlistData.name,
        isPrivate: watchlistData.isPrivate
      });

      // NOTE: In-app notification is now created by the onCollaborationRequestCreated Cloud Function
      // This ensures the notification is created server-side, bypassing ad blockers that block
      // client-side Firestore writes. Email notification is also handled by the Cloud Function.

      console.log('✅ Collaboration request sent successfully:', requestId);
      return requestId;
    } catch (error) {
      console.error('Error sending collaboration request:', error);
      throw error;
    }
  }

  /**
   * Accept a collaboration request
   */
  static async acceptCollaborationRequest(requestId: string, userId: string): Promise<void> {
    try {
      console.log('✅ AcceptCollaborationRequest: Starting', { requestId, userId });

      // Use unified getDoc for cross-platform support
      const requestDoc = await unifiedGetDoc('collaborationRequests', requestId);
      console.log('✅ AcceptCollaborationRequest: Fetched request doc', { exists: requestDoc.exists() });

      if (!requestDoc.exists()) {
        throw new Error('Collaboration request not found');
      }

      const request = { id: requestDoc.id, ...requestDoc.data() } as CollaborationRequest;
      console.log('✅ AcceptCollaborationRequest: Request data', {
        id: request.id,
        status: request.status,
        toUserId: request.toUserId,
        fromUserId: request.fromUserId,
        watchlistId: request.watchlistId
      });

      // Verify the user is the recipient
      if (request.toUserId !== userId) {
        throw new Error('Permission denied: You cannot accept this request');
      }

      // Check if request is still pending
      if (request.status !== 'pending') {
        throw new Error('Request is no longer pending');
      }

      // Add user as collaborator to the watchlist directly
      // This bypasses permission checks since the recipient is adding themselves
      const watchlistDoc = await unifiedGetDoc('watchlists', request.watchlistId);
      console.log('✅ AcceptCollaborationRequest: Fetched watchlist doc', { exists: watchlistDoc.exists() });

      if (!watchlistDoc.exists()) {
        throw new Error('Watchlist not found');
      }

      const watchlistData = watchlistDoc.data();
      const currentCollaborators = watchlistData.collaborators || [];
      console.log('✅ AcceptCollaborationRequest: Current collaborators', { currentCollaborators });

      // Check if already a collaborator
      if (currentCollaborators.includes(request.toUserId)) {
        throw new Error('You are already a collaborator on this watchlist');
      }

      // Add self as collaborator (and optional admin role from the invite)
      console.log('✅ AcceptCollaborationRequest: Updating watchlist with new collaborator');
      const prevAdmins = (watchlistData as { adminCollaboratorIds?: string[] }).adminCollaboratorIds || [];
      const adminCollaboratorIds =
        request.role === 'admin'
          ? [...new Set([...prevAdmins, request.toUserId])]
          : prevAdmins;

      const watchlistUpdate: Record<string, unknown> = {
        collaborators: [...currentCollaborators, request.toUserId],
        updatedAt: new Date().toISOString()
      };
      if (request.role === 'admin') {
        watchlistUpdate.adminCollaboratorIds = adminCollaboratorIds;
      }

      await unifiedUpdateDoc('watchlists', request.watchlistId, watchlistUpdate);
      console.log('✅ AcceptCollaborationRequest: Watchlist updated successfully');

      // Update request status
      console.log('✅ AcceptCollaborationRequest: Updating request status to accepted');
      await unifiedUpdateDoc('collaborationRequests', requestId, {
        status: 'accepted',
        acceptedAt: new Date().toISOString()
      });
      console.log('✅ AcceptCollaborationRequest: Request status updated successfully');

      // Get the acceptor's profile to show their name in the notification
      const acceptorProfile = await getCachedUserProfile(request.toUserId);
      const acceptorName = acceptorProfile?.displayName || 'Someone';
      console.log('✅ AcceptCollaborationRequest: Acceptor profile', { acceptorName });

      // Send notification to the requester
      console.log('✅ AcceptCollaborationRequest: Creating notification for requester');
      await socialService.createNotification({
        recipientId: request.fromUserId,
        type: 'collaborator_added',
        actorId: request.toUserId,
        actorName: acceptorName,
        actorAvatar: acceptorProfile?.photoURL,
        entityId: request.watchlistId,
        entityName: request.watchlistName,
        message: `${acceptorName} accepted your collaboration request for "${request.watchlistName}"`,
        metadata: {
          action: 'request_accepted',
          requestId
        }
      });
      console.log('✅ AcceptCollaborationRequest: Notification created successfully');

      console.log('✅ AcceptCollaborationRequest: Completed successfully');
    } catch (error) {
      console.error('❌ AcceptCollaborationRequest: Error', error);
      throw error;
    }
  }

  /**
   * Decline a collaboration request
   */
  static async declineCollaborationRequest(requestId: string, userId: string): Promise<void> {
    try {
      console.log('❌ DeclineCollaborationRequest: Starting', { requestId, userId });

      // Use unified getDoc for cross-platform support
      const requestDoc = await unifiedGetDoc('collaborationRequests', requestId);
      console.log('❌ DeclineCollaborationRequest: Fetched request doc', { exists: requestDoc.exists() });

      if (!requestDoc.exists()) {
        throw new Error('Collaboration request not found');
      }

      const request = { id: requestDoc.id, ...requestDoc.data() } as CollaborationRequest;
      console.log('❌ DeclineCollaborationRequest: Request data', {
        id: request.id,
        status: request.status,
        toUserId: request.toUserId,
        fromUserId: request.fromUserId,
        watchlistId: request.watchlistId
      });

      // Verify the user is the recipient
      if (request.toUserId !== userId) {
        throw new Error('Permission denied: You cannot decline this request');
      }

      // Update request status
      console.log('❌ DeclineCollaborationRequest: Updating request status to declined');
      await unifiedUpdateDoc('collaborationRequests', requestId, {
        status: 'declined',
        declinedAt: new Date().toISOString()
      });
      console.log('❌ DeclineCollaborationRequest: Request status updated successfully');

      // Optionally send notification to the requester
      console.log('❌ DeclineCollaborationRequest: Creating notification for requester');
      await socialService.createNotification({
        recipientId: request.fromUserId,
        type: 'collaboration',
        actorId: request.toUserId,
        actorName: request.fromUserName,
        actorAvatar: request.fromUserAvatar,
        entityId: request.watchlistId,
        entityName: request.watchlistName,
        message: `Your collaboration request for "${request.watchlistName}" was declined`,
        metadata: {
          action: 'request_declined',
          requestId
        }
      });
      console.log('❌ DeclineCollaborationRequest: Notification created successfully');

      console.log('❌ DeclineCollaborationRequest: Completed successfully');
    } catch (error) {
      console.error('❌ DeclineCollaborationRequest: Error', error);
      throw error;
    }
  }

  /**
   * Get pending requests for a user
   */
  static async getPendingRequestsForUser(userId: string): Promise<CollaborationRequest[]> {
    try {
      const docs = await unifiedCollection('collaborationRequests')
        .where('toUserId', '==', userId)
        .where('status', '==', 'pending')
        .limit(20)
        .getDocs();

      return docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as CollaborationRequest));

    } catch (error) {
      console.error('Error getting pending requests:', error);
      return [];
    }
  }

  /**
   * Get pending request for specific watchlist and user
   */
  static async getPendingRequest(watchlistId: string, userId: string): Promise<CollaborationRequest | null> {
    try {
      const docs = await unifiedCollection('collaborationRequests')
        .where('watchlistId', '==', watchlistId)
        .where('toUserId', '==', userId)
        .where('status', '==', 'pending')
        .getDocs();

      if (docs.length === 0) return null;

      const doc = docs[0];
      return { id: doc.id, ...doc.data() } as CollaborationRequest;

    } catch (error) {
      console.error('Error getting pending request:', error);
      return null;
    }
  }

  /**
   * Cancel a collaboration request (by sender)
   */
  static async cancelCollaborationRequest(requestId: string, userId: string): Promise<void> {
    try {
      // Use unified getDoc for cross-platform support
      const requestDoc = await unifiedGetDoc('collaborationRequests', requestId);

      if (!requestDoc.exists()) {
        throw new Error('Collaboration request not found');
      }

      const request = { id: requestDoc.id, ...requestDoc.data() } as CollaborationRequest;

      // Verify the user is the sender
      if (request.fromUserId !== userId) {
        throw new Error('Permission denied: You cannot cancel this request');
      }

      // Delete the request using unified deleteDoc for cross-platform support
      await unifiedDeleteDoc('collaborationRequests', requestId);

    } catch (error) {
      console.error('Error canceling collaboration request:', error);
      throw error;
    }
  }

  /**
   * Get sent requests by a user
   */
  static async getSentRequests(userId: string): Promise<CollaborationRequest[]> {
    try {
      const docs = await unifiedCollection('collaborationRequests')
        .where('fromUserId', '==', userId)
        .getDocs();

      return docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as CollaborationRequest));

    } catch (error) {
      console.error('Error getting sent requests:', error);
      return [];
    }
  }

  /**
   * Cleanup expired requests
   */
  static async cleanupExpiredRequests(): Promise<void> {
    try {
      const docs = await unifiedCollection('collaborationRequests')
        .where('status', '==', 'pending')
        .where('expiresAt', '<', new Date())
        .limit(50)
        .getDocs();

      const deletePromises = docs.map(doc =>
        unifiedUpdateDoc('collaborationRequests', doc.id, { status: 'expired' })
      );

      await Promise.all(deletePromises);

    } catch (error) {
      console.error('Error cleaning up expired requests:', error);
    }
  }
} 